import React, { useEffect, useState } from 'react';
import { Alert, Spin, Tree, Typography } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { apiClient } from '../../services/apiClient';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { nestProductModels, type ProductModelNode } from '../AppShell/platformDirectory';

const { Text } = Typography;

function toTreeData(nodes: ProductModelNode[]): DataNode[] {
  return nodes.map((node) => ({
    key: node.id,
    title: node.name,
    children: node.children.length > 0 ? toTreeData(node.children) : undefined,
  }));
}

/** Artifact trees for one company, nested when a parent tree id is present. */
export const CompanyProductModelsTab: React.FC<{ companyId: string; companyName: string }> = ({
  companyId,
  companyName,
}) => {
  const [nodes, setNodes] = useState<ProductModelNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .get<
        Array<{
          id?: string;
          name?: string;
          artifact_count?: number;
          parent_id?: string | null;
          parent_tree_id?: string | null;
          metadata?: Record<string, unknown> | null;
        }>
      >(
        '/artifacts/trees',
        { params: { company_id: companyId } },
      )
      .then((response) => {
        if (cancelled) return;
        const rows = Array.isArray(response.data) ? response.data : [];
        setNodes(
          nestProductModels(
            rows.map((row) => ({
              ...row,
              name:
                row.artifact_count && row.artifact_count > 0
                  ? `${row.name || row.id} (${row.artifact_count})`
                  : row.name,
            })),
          ),
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setNodes([]);
        setError(getApiErrorMessage(err, 'Could not load product models for this company.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  if (error) return <Alert type="error" showIcon message={error} />;
  if (loading && nodes.length === 0) return <Spin />;
  if (nodes.length === 0) {
    return <Text type="secondary">No product models for {companyName}.</Text>;
  }
  return (
    <>
      <Text type="secondary">
        Each row is an artifact tree for this company. A parent tree id, when the row has one, nests that tree.
        Companies do not have a parent company column.
      </Text>
      <Tree treeData={toTreeData(nodes)} defaultExpandAll />
    </>
  );
};
